'use client';

import { c as _c } from "react/compiler-runtime";
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/common/button';
import { ScrollArea } from '@/components/common/scroll-area';
import FilterSection from '@/components/features/LocationSearch/FilterSection';
import LocationCard from '@/components/features/LocationSearch/LocationCard';
import LocationsMap from '@/components/features/LocationSearch/LocationsMap';
import { useLocationFilters } from '@/components/features/LocationSearch/useLocationFilters';
import { useMediaQuery } from '@/lib/hooks/useMediaQuery';
import { filterLocationsByRegion, normalizeRegionName } from '@/lib/utils/locationSearch';
import { cn } from '@/lib/utils/utils';
import type { NurserySearchBlockType } from '@/sanity/lib/types/blockTypes';
import type { ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT } from '@/sanity/types';
import { useScrollNavigation } from './useScrollNavigation';
import { MobileFilterDrawer } from './MobileFilterDrawer';
import { FloatingFilterButton } from './FloatingFilterButton';
const DEFAULT_RADIUS = 30;
interface LocationSearchBlockProps extends NurserySearchBlockType {
  initialLocations: ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT;
}
export default function LocationSearchBlock(t0) {
  const $ = _c(76);
  let initialLocations;
  let props;
  if ($[0] !== t0) {
    ({
      initialLocations,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = initialLocations;
    $[2] = props;
  } else {
    initialLocations = $[1];
    props = $[2];
  }
  const {
    regionsFilters,
    regionsPreSelected,
    amenities,
    includeMap,
    includeSearch
  } = props;
  const locations = initialLocations;
  let t1;
  bb0: {
    if (!regionsPreSelected) {
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[4] !== regionsPreSelected) {
      t2 = regionsPreSelected.map(_temp).filter(_temp2);
      $[4] = regionsPreSelected;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = t2;
  }
  const preselectedRegionNames = t1;
  let t2;
  bb1: {
    if (preselectedRegionNames.length === 0) {
      t2 = locations;
      break bb1;
    }
    let t3;
    if ($[6] !== locations || $[7] !== preselectedRegionNames) {
      t3 = filterLocationsByRegion(locations, preselectedRegionNames);
      $[6] = locations;
      $[7] = preselectedRegionNames;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = t3;
  }
  const baseLocations = t2;
  const {
    filters,
    filteredLocations,
    toggleRegion,
    toggleAmenity,
    setSearchLocation,
    setSearchRadius,
    clearFilters
  } = useLocationFilters(baseLocations, preselectedRegionNames);
  const isMobile = !useMediaQuery("(min-width: 768px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [locationInputValue, setLocationInputValue] = useState(filters.searchLocation?.formattedAddress || "");
  const [showFloatingFilter, setShowFloatingFilter] = useState(false);
  const [showMap, setShowMap] = useState(includeMap ?? false);
  const resultsSectionRef = useRef(null);
  const t3 = !!isMobile;
  let t4;
  if ($[9] !== filteredLocations.length || $[10] !== t3) {
    t4 = {
      itemCount: filteredLocations.length,
      isMobile: t3
    };
    $[9] = filteredLocations.length;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    scrollAreaRef,
    firstCardRef,
    canScrollUp,
    canScrollDown,
    cardHeight,
    scrollUp,
    scrollDown
  } = useScrollNavigation(t4);
  let t5;
  if ($[12] !== filters.searchLocation?.formattedAddress) {
    t5 = () => {
      setLocationInputValue(filters.searchLocation?.formattedAddress || "");
    };
    $[12] = filters.searchLocation?.formattedAddress;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== filters.searchLocation) {
    t6 = [filters.searchLocation];
    $[14] = filters.searchLocation;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== isMobile) {
    t7 = () => {
      if (!isMobile || !resultsSectionRef.current) {
        setShowFloatingFilter(false);
        return;
      }
      const observer = new IntersectionObserver(entries => {
        setShowFloatingFilter(entries[0]?.isIntersecting ?? false);
      }, {
        threshold: 0.1,
        rootMargin: "0px"
      });
      observer.observe(resultsSectionRef.current);
      return () => {
        observer.disconnect();
      };
    };
    $[16] = isMobile;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== filteredLocations.length || $[19] !== isMobile) {
    t8 = [isMobile, filteredLocations.length];
    $[18] = filteredLocations.length;
    $[19] = isMobile;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useEffect(t7, t8);
  let count;
  count = 0 + filters.selectedRegions.length;
  count = count + filters.selectedAmenities.length;
  if (filters.searchLocation) {
    count = count + 1;
  }
  if (filters.searchRadius !== DEFAULT_RADIUS) {
    count = count + 1;
  }
  const activeFilterCount = count;
  let t9;
  bb2: {
    if (preselectedRegionNames.length === 0) {
      t9 = activeFilterCount > 0;
      break bb2;
    }
    let t10;
    if ($[21] !== filters.selectedRegions || $[22] !== preselectedRegionNames) {
      const selectedRegionsSorted = [...filters.selectedRegions].sort();
      let t11;
      if ($[24] !== preselectedRegionNames) {
        t11 = [...preselectedRegionNames].sort();
        $[24] = preselectedRegionNames;
        $[25] = t11;
      } else {
        t11 = $[25];
      }
      const preselectedRegionsSorted = t11;
      t10 = selectedRegionsSorted.length !== preselectedRegionsSorted.length || selectedRegionsSorted.some((region_0, index) => region_0 !== preselectedRegionsSorted[index]);
      $[21] = filters.selectedRegions;
      $[22] = preselectedRegionNames;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    const regionsDiffer = t10;
    t9 = regionsDiffer || filters.selectedAmenities.length > 0 || filters.searchLocation !== null || filters.searchRadius !== DEFAULT_RADIUS;
  }
  const hasModifiedFilters = t9;
  const t10 = regionsFilters || null;
  const t11 = amenities || null;
  let t12;
  if ($[26] !== clearFilters || $[27] !== filters.searchLocation || $[28] !== filters.searchRadius || $[29] !== filters.selectedAmenities || $[30] !== filters.selectedRegions || $[31] !== setSearchLocation || $[32] !== setSearchRadius || $[33] !== showMap || $[34] !== t10 || $[35] !== t11 || $[36] !== toggleAmenity || $[37] !== toggleRegion) {
    t12 = {
      selectedRegions: filters.selectedRegions,
      selectedAmenities: filters.selectedAmenities,
      searchLocation: filters.searchLocation,
      searchRadius: filters.searchRadius,
      regions: t10,
      amenities: t11,
      showMap,
      onRegionToggle: toggleRegion,
      onAmenityToggle: toggleAmenity,
      onSearchLocationChange: setSearchLocation,
      onSearchRadiusChange: setSearchRadius,
      onShowMapChange: setShowMap,
      onClearFilters: clearFilters
    };
    $[26] = clearFilters;
    $[27] = filters.searchLocation;
    $[28] = filters.searchRadius;
    $[29] = filters.selectedAmenities;
    $[30] = filters.selectedRegions;
    $[31] = setSearchLocation;
    $[32] = setSearchRadius;
    $[33] = showMap;
    $[34] = t10;
    $[35] = t11;
    $[36] = toggleAmenity;
    $[37] = toggleRegion;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const filterSectionProps = t12;
  let t13;
  if ($[39] !== filterSectionProps || $[40] !== includeSearch || $[41] !== isMobile) {
    t13 = includeSearch !== false && !isMobile && <FilterSection {...filterSectionProps} />;
    $[39] = filterSectionProps;
    $[40] = includeSearch;
    $[41] = isMobile;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== clearFilters || $[44] !== drawerOpen || $[45] !== filterSectionProps || $[46] !== includeSearch || $[47] !== isMobile || $[48] !== locationInputValue) {
    t14 = includeSearch !== false && isMobile && <MobileFilterDrawer open={drawerOpen} onOpenChange={setDrawerOpen} filterSectionProps={filterSectionProps} locationInputValue={locationInputValue} onClearFilters={clearFilters} onOpenDrawer={() => setDrawerOpen(true)} />;
    $[43] = clearFilters;
    $[44] = drawerOpen;
    $[45] = filterSectionProps;
    $[46] = includeSearch;
    $[47] = isMobile;
    $[48] = locationInputValue;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== showMap) {
    t15 = cn("mx-auto max-w-7xl overflow-hidden px-5 md:py-20 flex flex-col md:gap-20", {
      "py-10": showMap
    });
    $[50] = showMap;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== canScrollDown || $[53] !== canScrollUp || $[54] !== cardHeight || $[55] !== filteredLocations || $[56] !== firstCardRef || $[57] !== scrollAreaRef || $[58] !== scrollDown || $[59] !== scrollUp || $[60] !== showMap) {
    t16 = filteredLocations.length === 0 ? <div className="text-center text-lg text-primary-blue">No locations found matching your criteria.</div> : <>{showMap && <LocationsMap locations={filteredLocations} />}<div className="relative" ref={scrollAreaRef}><ScrollArea className="h-[600px] md:h-[550px]" type="always"><div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:items-stretch py-10 md:py-0">{filteredLocations.map((location, index_0) => <div key={location._id} ref={index_0 === 0 ? el => {
              firstCardRef.current = el;
            } : undefined}><LocationCard location={location} /></div>)}</div></ScrollArea>{filteredLocations.length > 0 && cardHeight > 0 && <>{canScrollUp && <Button size="icon" onClick={scrollUp} aria-label="Scroll up one location" className="absolute right-4 top-4 z-10 h-10 w-10 rounded-full shadow-lg"><ChevronUp className="size-5" /></Button>}{canScrollDown && <Button size="icon" onClick={scrollDown} aria-label="Scroll down one location" className="absolute bottom-4 right-4 z-10 h-10 w-10 rounded-full shadow-lg"><ChevronDown className="size-5" /></Button>}</>}</div></>;
    $[52] = canScrollDown;
    $[53] = canScrollUp;
    $[54] = cardHeight;
    $[55] = filteredLocations;
    $[56] = firstCardRef;
    $[57] = scrollAreaRef;
    $[58] = scrollDown;
    $[59] = scrollUp;
    $[60] = showMap;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== t15 || $[63] !== t16) {
    t17 = <div ref={resultsSectionRef} id="search-results" className={t15}>{t16}</div>;
    $[62] = t15;
    $[63] = t16;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== activeFilterCount || $[66] !== clearFilters || $[67] !== hasModifiedFilters || $[68] !== isMobile || $[69] !== showFloatingFilter) {
    t18 = isMobile && showFloatingFilter && <FloatingFilterButton onOpenDrawer={() => setDrawerOpen(true)} onClearFilters={() => {
      clearFilters();
      setLocationInputValue("");
    }} activeFilterCount={activeFilterCount} hasModifiedFilters={hasModifiedFilters} />;
    $[65] = activeFilterCount;
    $[66] = clearFilters;
    $[67] = hasModifiedFilters;
    $[68] = isMobile;
    $[69] = showFloatingFilter;
    $[70] = t18;
  } else {
    t18 = $[70];
  }
  let t19;
  if ($[71] !== t13 || $[72] !== t14 || $[73] !== t17 || $[74] !== t18) {
    t19 = <div className="flex flex-col">{t13}{t14}{t17}{t18}</div>;
    $[71] = t13;
    $[72] = t14;
    $[73] = t17;
    $[74] = t18;
    $[75] = t19;
  } else {
    t19 = $[75];
  }
  return t19;
}
function _temp2(name) {
  return Boolean(name);
}
function _temp(region) {
  return normalizeRegionName(region.name || "");
}